import axios from 'axios';
import {
  AuthResponse,
  CategoryType,
  ContentCategory,
  LoginCredentials,
  User,
  Slider,
  Article,
  Podcast,
  VideoPodcast,
  Course,
  Video,
  Audio,
  Workshop,
  WorkshopParticipant,
  Log,
  UploadedFileInfo,
  Lead,
  LeadStatus,
  LeadStats,
  AdminCourseChapter,
  AdminCourseLessonUpdate,
  AdminCoursePracticeSession,
  AdminCourseFaq,
  AdminCourseSatisfactionStory,
  CourseFaqCategoryAdmin,
} from '../types';
import { ApiError } from '../contexts/ErrorContext';

const DEFAULT_SERVER_API = 'https://api.manehaghighi.com/api';

const normalizeUrl = (url: string) => url.replace(/\/$/, '');

const isLocalHost = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1';

/** دسترسی از موبایل/شبکه با IP (مثلاً 192.168.x.x) — بدون پروکسی درخواست به /api گم می‌شود */
const isPrivateLanHost = (host: string) =>
  /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
  /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
  /^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(host);

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();

  if (typeof window === 'undefined') {
    return envUrl ? normalizeUrl(envUrl) : DEFAULT_SERVER_API;
  }

  const hostname = window.location.hostname;

  /** اتصال مستقیم به Nest روی پورت ۳۰۰۰ — هم npm start هم build+serve لوکال و هم IP شبکه (مسیر نسبی /api روی سرور استاتیک 404 می‌دهد). */
  const useDirectLocalBackend =
    process.env.NODE_ENV === 'development' ||
    isLocalHost(hostname) ||
    isPrivateLanHost(hostname);

  if (useDirectLocalBackend) {
    if (
      envUrl &&
      isLocalHost(hostname) &&
      (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))
    ) {
      return normalizeUrl(envUrl);
    }
    const backendHost = isLocalHost(hostname) ? '127.0.0.1' : hostname;
    return `http://${backendHost}:3000/api`;
  }

  // سرور عمومی؛ nginx معمولاً /api را به بک‌اند می‌فرستد
  if (envUrl) {
    const url = normalizeUrl(envUrl);
    return url.replace(/^http:\/\//, 'https://');
  }
  return '/api';
};

export const API_BASE_URL = getApiBaseUrl();
export const API_ORIGIN =
  API_BASE_URL === '/api' || API_BASE_URL.startsWith('/')
    ? (typeof window !== 'undefined' ? window.location.origin : '')
    : API_BASE_URL.endsWith('/api')
      ? API_BASE_URL.slice(0, -4)
      : API_BASE_URL;

// Global error handler that can be set by React components
let globalErrorHandler: ((error: ApiError) => void) | null = null;

export const setGlobalErrorHandler = (handler: (error: ApiError) => void) => {
  globalErrorHandler = handler;
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // FormData needs a multipart boundary; a bare "multipart/form-data" header breaks multer (no file).
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const h = config.headers as { delete?: (n: string) => void } & Record<string, unknown>;
    if (typeof h?.delete === 'function') {
      h.delete('Content-Type');
    } else {
      delete h['Content-Type'];
      delete h['content-type'];
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Handle 304 Not Modified - ensure response.data exists
    if (response.status === 304 && !response.data) {
      // For 304, we should use cached data if available, or return empty array/object
      // This depends on the endpoint, but we'll let the service handle it
      response.data = response.data || null;
    }
    return response;
  },
  (error) => {
    // Handle API errors globally
    if (error.response?.data) {
      const apiError: ApiError = {
        message: error.response.data.message || 'خطای نامشخص سرور',
        statusCode: error.response.data.statusCode,
        path: error.response.data.path,
        method: error.response.data.method,
        timestamp: error.response.data.timestamp,
        error: error.response.data.error,
        stack: error.response.data.stack,
      };

      // Call global error handler if set
      if (globalErrorHandler) {
        globalErrorHandler(apiError);
      }
    }

    if (error.response?.status === 401) {
      const message = error.response?.data?.message;
      if (typeof message === 'string' && message.trim()) {
        try {
          sessionStorage.setItem('login_401_message', message.trim());
        } catch (_) {}
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  changePassword: async (data: { currentPassword?: string; newPassword: string; confirmPassword?: string }): Promise<{ message: string }> => {
    const response = await api.patch('/auth/password', {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword ?? data.newPassword,
    });
    return response.data;
  },
};

export const categoriesService = {
  getAll: async (type?: CategoryType): Promise<ContentCategory[]> => {
    const response = await api.get('/categories', {
      params: type ? { type } : undefined,
    });
    return response.data;
  },

  getTree: async (type: CategoryType): Promise<ContentCategory[]> => {
    const response = await api.get('/categories/tree', {
      params: { type },
    });
    return response.data;
  },

  getById: async (id: string): Promise<ContentCategory> => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  create: async (payload: Partial<ContentCategory> & { name: string; slug: string; type: CategoryType }): Promise<ContentCategory> => {
    const response = await api.post('/categories', payload);
    return response.data;
  },

  update: async (id: string, payload: Partial<ContentCategory>): Promise<ContentCategory> => {
    const response = await api.patch(`/categories/${id}`, payload);
    return response.data;
  },

  move: async (id: string, parentId: string | null): Promise<ContentCategory> => {
    const response = await api.patch(`/categories/${id}/move`, { parentId });
    return response.data;
  },

  remove: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const usersService = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; role?: string }): Promise<{ data: User[]; meta: { total: number; page: number; limit: number; totalPages: number } }> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (data: Partial<User>): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data;
  },

  update: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  blockUser: async (id: string): Promise<User> => {
    const response = await api.post(`/users/${id}/block`);
    return response.data;
  },

  unblockUser: async (id: string): Promise<User> => {
    const response = await api.post(`/users/${id}/unblock`);
    return response.data;
  },

  getUserCourses: async (id: string) => {
    const response = await api.get(`/users/${id}/courses`);
    return response.data;
  },


  getUserWithProducts: async (id: string) => {
    const response = await api.get(`/users/with-products/${id}`);
    return response.data;
  },

  assignCourses: async (userId: string, courseIds: string[]) => {
    const response = await api.post(`/users/${userId}/courses`, { courseIds });
    return response.data;
  },

  grantVideoAccess: async (userId: string, videoId: string) => {
    const response = await api.post(`/users/${userId}/video-access/${videoId}`);
    return response.data;
  },

  revokeVideoAccess: async (userId: string, videoId: string) => {
    await api.delete(`/users/${userId}/video-access/${videoId}`);
  },

  getSalesPersons: async (): Promise<User[]> => {
    const response = await api.get('/users/sales-persons');
    return response.data;
  },

  getSalesManagers: async (): Promise<User[]> => {
    const response = await api.get('/users/sales-managers');
    return response.data;
  },

  assignSalesPersonToManager: async (salesPersonId: string, salesManagerId: string) => {
    const response = await api.post('/users/assign-sales-person', {
      salesPersonId,
      salesManagerId,
    });
    return response.data;
  },

  unassignSalesPersonFromManager: async (salesPersonId: string) => {
    const response = await api.delete(`/users/unassign-sales-person/${salesPersonId}`);
    return response.data;
  },

  exportUsers: async (filters?: {
    userType?: 'all' | 'old' | 'new';
    startDate?: string;
    endDate?: string;
    role?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.userType && filters.userType !== 'all') {
      params.append('userType', filters.userType);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.role) {
      params.append('role', filters.role);
    }

    const response = await api.get(`/users/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  exportUsersJson: async (filters?: {
    userType?: 'all' | 'old' | 'new';
    startDate?: string;
    endDate?: string;
    role?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.userType && filters.userType !== 'all') {
      params.append('userType', filters.userType);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.role) {
      params.append('role', filters.role);
    }

    const url = `/users/export/json${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url, {
      responseType: 'blob',
      timeout: 120000, // 2 min for large exports
    });
    return response.data;
  },

  exportUserJson: async (userId: string) => {
    const response = await api.get(`/users/${userId}/export/json`, {
      responseType: 'blob',
    });
    return response.data;
  },

  checkPhoneExists: async (phone: string): Promise<{ exists: boolean }> => {
    const response = await api.get(`/users/check-phone/${phone}`);
    return response.data;
  },

  getUserByPhone: async (phone: string): Promise<{ id: string; firstName: string; lastName: string; phone: string }> => {
    const response = await api.get(`/users/by-phone/${phone}`);
    return response.data;
  },

  createCustomer: async (data: {
    firstName: string;
    lastName: string;
    phone: string;
    password: string;
    confirmPassword: string;
    role: string;
  }): Promise<User> => {
    const response = await api.post('/users/customer', data);
    return response.data;
  },

  getUsersByRole: async (role: string): Promise<User[]> => {
    const response = await api.get(`/users?role=${role}&limit=1000`);
    return response.data.data || [];
  },

  getSellerStats: async (): Promise<{
    totalLinks: number;
    paidLinks: number;
    unpaidLinks: number;
    totalRevenue: number;
    todayRevenue: number;
    workshopCount: number;
  }> => {
    const response = await api.get('/users/seller/stats');
    return response.data;
  },

  getManagerTeamStats: async (): Promise<{
    totalSellers: number;
    activeSellers: number;
    totalRevenue: number;
    todayRevenue: number;
    totalLinks: number;
    paidLinks: number;
    conversionRate: number;
  }> => {
    const response = await api.get('/users/manager/team-stats');
    return response.data;
  },

  getMySellers: async (): Promise<Array<{
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    phone: string;
    email?: string;
    isActive: boolean;
    totalLinks: number;
    paidLinks: number;
    totalRevenue: number;
    lastActivity: string;
  }>> => {
    const response = await api.get('/users/manager/my-sellers');
    return response.data;
  },
};

export const messagesService = {
  broadcast: async (data: { title: string; body: string; sendSms?: boolean; sendInApp?: boolean }) => {
    const response = await api.post('/messages/broadcast', data);
    return response.data;
  },
  list: async () => {
    const response = await api.get('/messages');
    return response.data;
  },
};

export const slidersService = {
  getAll: async (): Promise<Slider[]> => {
    const response = await api.get('/sliders');
    return response.data;
  },

  getActive: async (): Promise<Slider[]> => {
    const response = await api.get('/sliders/active');
    return response.data;
  },

  getById: async (id: string): Promise<Slider> => {
    const response = await api.get(`/sliders/${id}`);
    return response.data;
  },

  create: async (data: Omit<Slider, 'id' | 'createdAt' | 'updatedAt'>): Promise<Slider> => {
    const response = await api.post('/sliders', data);
    return response.data;
  },

  createWithFiles: async (formData: FormData, onProgress?: (progressEvent: any) => void): Promise<Slider> => {
    const response = await api.post('/sliders', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود تصویر/ویدیوی اسلایدر ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Slider>): Promise<Slider> => {
    const response = await api.patch(`/sliders/${id}`, data);
    return response.data;
  },

  updateWithFiles: async (id: string, formData: FormData, onProgress?: (progressEvent: any) => void): Promise<Slider> => {
    const response = await api.patch(`/sliders/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود تصویر/ویدیوی اسلایدر ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/sliders/${id}`);
  },

  bulkUpdate: async (sliderIds: string[], data: Partial<Slider>) => {
    const response = await api.patch('/sliders/bulk', { sliderIds, data });
    return response.data;
  },

  bulkDelete: async (sliderIds: string[]) => {
    const response = await api.delete('/sliders/bulk', { data: { sliderIds } });
    return response.data;
  },

  activate: async (id: string) => {
    const response = await api.patch(`/sliders/${id}/activate`);
    return response.data;
  },

  deactivate: async (id: string) => {
    const response = await api.patch(`/sliders/${id}/deactivate`);
    return response.data;
  },

  reorder: async (sliderIds: string[]) => {
    const response = await api.patch('/sliders/reorder', { sliderIds });
    return response.data;
  },

  search: async (query: string, filters?: any) => {
    const response = await api.get('/sliders/search', { 
      params: { q: query, ...filters } 
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/sliders/stats');
    return response.data;
  },

  exportSliders: async (format: 'csv' | 'excel' = 'csv') => {
    const response = await api.get(`/sliders/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export const articlesService = {
  getAll: async (params?: any): Promise<any> => {
    const response = await api.get('/articles', { params });
    return response.data;
  },

  getPublished: async (params?: any): Promise<any> => {
    const response = await api.get('/articles/published', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Article> => {
    const response = await api.get(`/articles/${id}`);
    return response.data;
  },

  getBySlug: async (slug: string): Promise<Article> => {
    const response = await api.get(`/articles/slug/${slug}`);
    return response.data;
  },

  create: async (data: any): Promise<Article> => {
    const response = await api.post('/articles', data);
    return response.data;
  },

  update: async (id: string, data: any): Promise<Article> => {
    const response = await api.patch(`/articles/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/articles/${id}`);
  },

  uploadFeaturedImage: async (id: string, file: File, onProgress?: (progressEvent: any) => void): Promise<Article> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.patch(`/articles/${id}/featured-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  analyzeSeo: async (id: string): Promise<any> => {
    const response = await api.get(`/articles/${id}/seo-analysis`);
    return response.data;
  },

  generateSchema: async (id: string): Promise<any> => {
    const response = await api.get(`/articles/${id}/schema`);
    return response.data;
  },

  getRelated: async (id: string, limit?: number): Promise<Article[]> => {
    const response = await api.get(`/articles/${id}/related`, { params: { limit } });
    return response.data;
  },

  // Categories
  getAllCategories: async (): Promise<any[]> => {
    const response = await api.get('/articles/categories/all');
    return response.data;
  },

  createCategory: async (data: any): Promise<any> => {
    const response = await api.post('/articles/categories', data);
    return response.data;
  },

  updateCategory: async (id: string, data: any): Promise<any> => {
    const response = await api.patch(`/articles/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: string): Promise<void> => {
    await api.delete(`/articles/categories/${id}`);
  },

  bulkUpdate: async (articleIds: string[], data: Partial<Article>) => {
    const response = await api.patch('/articles/bulk', { articleIds, data });
    return response.data;
  },

  bulkDelete: async (articleIds: string[]) => {
    const response = await api.delete('/articles/bulk', { data: { articleIds } });
    return response.data;
  },

  publish: async (id: string) => {
    const response = await api.patch(`/articles/${id}/publish`);
    return response.data;
  },

  unpublish: async (id: string) => {
    const response = await api.patch(`/articles/${id}/unpublish`);
    return response.data;
  },

  /** جست‌وجوی ادمین (همان لیست با پارامتر search) */
  search: async (query: string, filters?: any) => {
    const response = await api.get('/articles', {
      params: { search: query, ...filters },
    });
    return response.data;
  },

  /** شمارنده‌های وضعیت برای تب‌های پنل وبلاگ */
  getStats: async (): Promise<ArticleAdminStats> => {
    const response = await api.get('/articles/admin/stats');
    return response.data;
  },

  /** همهٔ دسته‌ها (فعال و غیرفعال) با شمارنده‌ها */
  getManageCategories: async (): Promise<ArticleCategoryAdmin[]> => {
    const response = await api.get('/articles/categories/manage');
    return Array.isArray(response.data) ? response.data : [];
  },

  reorderCategories: async (ids: string[]): Promise<ArticleCategoryAdmin[]> => {
    const response = await api.patch('/articles/categories/reorder', { ids });
    return response.data;
  },

  getTags: async (): Promise<ArticleTagUsage[]> => {
    const response = await api.get('/articles/tags');
    return Array.isArray(response.data) ? response.data : [];
  },

  renameTag: async (from: string, to: string): Promise<{ updated: number }> => {
    const response = await api.patch('/articles/tags/rename', { from, to });
    return response.data;
  },

  deleteTag: async (tag: string): Promise<{ updated: number }> => {
    const response = await api.delete(`/articles/tags/${encodeURIComponent(tag)}`);
    return response.data;
  },

  /** متن‌ها و لینک‌های ثابت صفحه‌های وبلاگ (هدر، فوتر، عنوان بخش‌ها) */
  getSettings: async (): Promise<BlogSettingsData> => {
    const response = await api.get('/articles/settings');
    return response.data;
  },

  updateSettings: async (data: Partial<BlogSettingsData>): Promise<BlogSettingsData> => {
    const response = await api.patch('/articles/settings', data);
    return response.data;
  },
};

export interface BlogLink {
  label: string;
  href: string;
}

export interface BlogSettingsData {
  brandName: string;
  navLinks: BlogLink[];
  loginLabel: string;
  loginHref: string;
  mobileMenuLoginLabel: string;
  heroTitle: string;
  heroSubtitle: string;
  searchPlaceholder: string;
  featuredTitle: string;
  featuredBadge: string;
  featuredBadgeMobile: string;
  allArticlesTitle: string;
  tocTitle: string;
  videoTitle: string;
  tagsTitle: string;
  feedbackTitle: string;
  feedbackSubtitle: string;
  commentsTitle: string;
  commentsSubtitle: string;
  commentsSubtitleMobile: string;
  commentFormTitle: string;
  commentNote: string;
  officialBadge: string;
  officialBadgeMobile: string;
  commentNamePlaceholder: string;
  commentNamePlaceholderMobile: string;
  commentTextPlaceholder: string;
  commentTextPlaceholderMobile: string;
  relatedTitle: string;
  relatedMoreLabel: string;
  footerTagline: string;
  footerTaglineMobileList: string;
  footerTaglineMobileDetail: string;
  copyright: string;
  footerLinks: BlogLink[];
  social: { youtube: string; instagram: string; telegram: string; email: string; podcast: string; rss: string };
}

export interface ArticleAdminStats {
  all: number;
  draft: number;
  scheduled: number;
  published: number;
  featured: number;
}

export interface ArticleCategoryAdmin {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  parentId?: string | null;
  order: number;
  isActive: boolean;
  articleCount: number;
  publishedCount: number;
}

export interface ArticleTagUsage {
  tag: string;
  count: number;
  publishedCount: number;
}

export const podcastsService = {
  getAll: async (): Promise<Podcast[]> => {
    const response = await api.get('/podcasts');
    return response.data;
  },

  getPublished: async (): Promise<Podcast[]> => {
    const response = await api.get('/podcasts/published');
    return response.data;
  },

  getById: async (id: string): Promise<Podcast> => {
    const response = await api.get(`/podcasts/${id}`);
    return response.data;
  },

  create: async (data: {
    title: string;
    description?: string;
    audioLink?: string;
    duration?: number | string;
    published?: boolean;
    audioFile?: File;
  }): Promise<Podcast> => {
    const formData = new FormData();
    formData.append('title', data.title);

    if (typeof data.published !== 'undefined') {
      formData.append('published', String(data.published));
    }

    if (data.description) {
      formData.append('description', data.description);
    }

    if (typeof data.duration !== 'undefined' && data.duration !== null && data.duration !== '') {
      formData.append('duration', String(data.duration));
    }

    if (data.audioLink) {
      formData.append('audioFile', data.audioLink);
    }

    if (data.audioFile) {
      formData.append('audio', data.audioFile);
    }

    return podcastsService.createWithFile(formData);
  },

  createWithFile: async (formData: FormData, onProgress?: (progressEvent: any) => void): Promise<Podcast> => {
    const response = await api.post('/podcasts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود فایل صوتی ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Podcast>): Promise<Podcast> => {
    const response = await api.patch(`/podcasts/${id}`, data);
    return response.data;
  },

  updateWithFile: async (id: string, formData: FormData, onProgress?: (progressEvent: any) => void): Promise<Podcast> => {
    const response = await api.patch(`/podcasts/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود فایل صوتی ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/podcasts/${id}`);
  },

  bulkUpdate: async (podcastIds: string[], data: Partial<Podcast>) => {
    const response = await api.patch('/podcasts/bulk', { podcastIds, data });
    return response.data;
  },

  bulkDelete: async (podcastIds: string[]) => {
    const response = await api.delete('/podcasts/bulk', { data: { podcastIds } });
    return response.data;
  },

  publish: async (id: string) => {
    const response = await api.patch(`/podcasts/${id}/publish`);
    return response.data;
  },

  unpublish: async (id: string) => {
    const response = await api.patch(`/podcasts/${id}/unpublish`);
    return response.data;
  },

  search: async (query: string, filters?: any) => {
    const response = await api.get('/podcasts/search', { 
      params: { q: query, ...filters } 
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/podcasts/stats');
    return response.data;
  },

  exportPodcasts: async (format: 'csv' | 'excel' = 'csv') => {
    const response = await api.get(`/podcasts/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export const videoPodcastsService = {
  getAll: async (): Promise<VideoPodcast[]> => {
    const response = await api.get('/video-podcasts');
    return response.data;
  },

  getPublished: async (): Promise<VideoPodcast[]> => {
    const response = await api.get('/video-podcasts/published');
    return response.data;
  },

  getById: async (id: string): Promise<VideoPodcast> => {
    const response = await api.get(`/video-podcasts/${id}`);
    return response.data;
  },

  create: async (data: {
    title: string;
    description?: string;
    videoLink?: string;
    thumbnail?: string;
    duration?: number | string;
    published?: boolean;
    publishedAt?: string;
    videoFile?: File;
  }): Promise<VideoPodcast> => {
    const formData = new FormData();
    formData.append('title', data.title);

    if (typeof data.published !== 'undefined') {
      formData.append('published', String(data.published));
    }

    if (data.description) {
      formData.append('description', data.description);
    }

    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail);
    }

    if (data.publishedAt) {
      formData.append('publishedAt', data.publishedAt);
    }

    if (typeof data.duration !== 'undefined' && data.duration !== null && data.duration !== '') {
      formData.append('duration', String(data.duration));
    }

    if (data.videoLink) {
      formData.append('videoFile', data.videoLink);
    }

    if (data.videoFile) {
      formData.append('video', data.videoFile);
    }

    return videoPodcastsService.createWithFile(formData);
  },

  createWithFile: async (formData: FormData, onProgress?: (progressEvent: any) => void): Promise<VideoPodcast> => {
    const response = await api.post('/video-podcasts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود ویدیو ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  update: async (id: string, data: {
    title?: string;
    description?: string;
    videoLink?: string;
    thumbnail?: string;
    duration?: number | string;
    published?: boolean;
    publishedAt?: string | null;
    videoFile?: File;
  }): Promise<VideoPodcast> => {
    const formData = new FormData();

    if (typeof data.title !== 'undefined') {
      formData.append('title', data.title);
    }

    if (typeof data.description !== 'undefined') {
      formData.append('description', data.description);
    }

    if (typeof data.thumbnail !== 'undefined') {
      formData.append('thumbnail', data.thumbnail);
    }

    if (typeof data.duration !== 'undefined' && data.duration !== null && data.duration !== '') {
      formData.append('duration', String(data.duration));
    }

    if (typeof data.published !== 'undefined') {
      formData.append('published', String(data.published));
    }

    if (typeof data.publishedAt !== 'undefined' && data.publishedAt !== null) {
      formData.append('publishedAt', data.publishedAt);
    }

    if (typeof data.videoLink !== 'undefined') {
      formData.append('videoFile', data.videoLink || '');
    }

    if (data.videoFile) {
      formData.append('video', data.videoFile);
    }

    return videoPodcastsService.updateWithFile(id, formData);
  },

  updateWithFile: async (id: string, formData: FormData, onProgress?: (progressEvent: any) => void): Promise<VideoPodcast> => {
    const response = await api.patch(`/video-podcasts/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود ویدیو ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/video-podcasts/${id}`);
  },
};

export const coursesService = {
  getAll: async (): Promise<Course[]> => {
    const response = await api.get('/courses');
    return response.data;
  },

  /** همه دوره‌ها با همه ویدیوها و صداها (ادمین، برای خروجی) */
  getExportFull: async (): Promise<Course[]> => {
    const response = await api.get('/courses/export-full');
    return response.data;
  },

  getPublished: async (): Promise<Course[]> => {
    const response = await api.get('/courses/published');
    return response.data;
  },

  getById: async (id: string): Promise<Course> => {
    const response = await api.get(`/courses/${id}`);
    return response.data;
  },

  create: async (data: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'videos' | '_count'>): Promise<Course> => {
    const response = await api.post('/courses', data);
    return response.data;
  },

  createWithFiles: async (formData: FormData, onProgress?: (progressEvent: any) => void): Promise<Course> => {
    const response = await api.post('/courses', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود ویدیو/فایل دوره ممکن است چند دقیقه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Course>): Promise<Course> => {
    const response = await api.patch(`/courses/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/courses/${id}`);
  },

  enrollUser: async (courseId: string, userId: string) => {
    const response = await api.post(`/courses/${courseId}/enroll`, { userId });
    return response.data;
  },

  unenrollUser: async (courseId: string, userId: string) => {
    await api.delete(`/courses/${courseId}/enroll/${userId}`);
  },

  getEnrollments: async (courseId: string) => {
    const response = await api.get(`/courses/${courseId}/enrollments`);
    return response.data;
  },

  transferEnrollments: async (courseId: string, targetCourseId: string) => {
    const response = await api.patch(`/courses/${courseId}/transfer-enrollments`, {
      targetCourseId,
    });
    return response.data;
  },

  getEnrolledUsers: async (courseId: string) => {
    const response = await api.get(`/courses/${courseId}/enrolled-users`);
    return response.data;
  },

  getCourseVideos: async (courseId: string) => {
    const response = await api.get(`/courses/${courseId}/videos`);
    return response.data;
  },

  addVideoToCourse: async (courseId: string, videoData: any) => {
    const response = await api.post(`/courses/${courseId}/videos`, videoData);
    return response.data;
  },

  bulkUpdate: async (courseIds: string[], data: Partial<Course>) => {
    const response = await api.patch('/courses/bulk', { courseIds, data });
    return response.data;
  },

  bulkDelete: async (courseIds: string[]) => {
    const response = await api.delete('/courses/bulk', { data: { courseIds } });
    return response.data;
  },

  publish: async (id: string) => {
    const response = await api.patch(`/courses/${id}/publish`);
    return response.data;
  },

  unpublish: async (id: string) => {
    const response = await api.patch(`/courses/${id}/unpublish`);
    return response.data;
  },

  search: async (query: string, filters?: any) => {
    const response = await api.get('/courses/search', { 
      params: { q: query, ...filters } 
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/courses/stats');
    return response.data;
  },

  exportCourses: async (format: 'csv' | 'excel' = 'csv') => {
    const response = await api.get(`/courses/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getCurriculumAdmin: async (courseId: string): Promise<AdminCourseChapter[]> => {
    const response = await api.get(`/courses/${courseId}/curriculum`);
    return response.data;
  },

  createChapter: async (
    courseId: string,
    body: { title: string; description?: string; sortOrder?: number },
  ) => {
    const response = await api.post(`/courses/${courseId}/chapters`, body);
    return response.data;
  },

  updateChapter: async (
    courseId: string,
    chapterId: string,
    body: { title?: string; description?: string; sortOrder?: number },
  ) => {
    const response = await api.patch(`/courses/${courseId}/chapters/${chapterId}`, body);
    return response.data;
  },

  deleteChapter: async (courseId: string, chapterId: string): Promise<void> => {
    await api.delete(`/courses/${courseId}/chapters/${chapterId}`);
  },

  createLesson: async (
    courseId: string,
    chapterId: string,
    body: { title: string; caption?: string; sortOrder?: number; published?: boolean },
  ) => {
    const response = await api.post(`/courses/${courseId}/chapters/${chapterId}/lessons`, body);
    return response.data;
  },

  updateLesson: async (
    courseId: string,
    lessonId: string,
    body: {
      title?: string;
      caption?: string;
      sortOrder?: number;
      published?: boolean;
      videoId?: string | null;
      audioId?: string | null;
    },
  ) => {
    const response = await api.patch(`/courses/${courseId}/lessons/${lessonId}`, body);
    return response.data;
  },

  deleteLesson: async (courseId: string, lessonId: string): Promise<void> => {
    await api.delete(`/courses/${courseId}/lessons/${lessonId}`);
  },

  getLessonUpdatesAdmin: async (courseId: string): Promise<AdminCourseLessonUpdate[]> => {
    const response = await api.get(`/courses/${courseId}/lesson-updates`);
    return response.data;
  },

  createLessonUpdate: async (courseId: string, formData: FormData): Promise<AdminCourseLessonUpdate> => {
    // آپلود صوت/ویدیو ممکن است چند دقیقه طول بکشد؛ timeout پیش‌فرض ۳۰ ثانیه را برای آپلود غیرفعال می‌کنیم.
    const response = await api.post(`/courses/${courseId}/lesson-updates`, formData, { timeout: 0 });
    return response.data;
  },

  patchLessonUpdate: async (
    courseId: string,
    updateId: string,
    formData: FormData,
  ): Promise<AdminCourseLessonUpdate> => {
    // همان دلیل createLessonUpdate — timeout پیش‌فرض برای آپلودهای بزرگ کافی نیست.
    const response = await api.patch(`/courses/${courseId}/lesson-updates/${updateId}`, formData, { timeout: 0 });
    return response.data;
  },

  deleteLessonUpdate: async (courseId: string, updateId: string): Promise<void> => {
    await api.delete(`/courses/${courseId}/lesson-updates/${updateId}`);
  },

  getPracticeSessionsAdmin: async (courseId: string): Promise<AdminCoursePracticeSession[]> => {
    const response = await api.get(`/courses/${courseId}/practice-sessions`);
    return response.data;
  },

  createPracticeSession: async (courseId: string, formData: FormData): Promise<AdminCoursePracticeSession> => {
    // آپلود جزوهٔ PDF (تا ۸۰ مگابایت) با timeout پیش‌فرض ۳۰ ثانیه نیمه‌کاره قطع می‌شد.
    const response = await api.post(`/courses/${courseId}/practice-sessions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
    return response.data;
  },

  patchPracticeSession: async (
    courseId: string,
    sessionId: string,
    formData: FormData,
  ): Promise<AdminCoursePracticeSession> => {
    const response = await api.patch(`/courses/${courseId}/practice-sessions/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
    return response.data;
  },

  deletePracticeSession: async (courseId: string, sessionId: string): Promise<void> => {
    await api.delete(`/courses/${courseId}/practice-sessions/${sessionId}`);
  },

  getFaqsAdmin: async (courseId: string): Promise<AdminCourseFaq[]> => {
    const response = await api.get(`/courses/${courseId}/faqs`);
    return response.data;
  },

  createFaq: async (
    courseId: string,
    body: {
      question: string;
      answer: string;
      category: CourseFaqCategoryAdmin;
      sortOrder?: number;
      published?: boolean;
    },
  ): Promise<AdminCourseFaq> => {
    const response = await api.post(`/courses/${courseId}/faqs`, body);
    return response.data;
  },

  patchFaq: async (
    courseId: string,
    faqId: string,
    body: Partial<{
      question: string;
      answer: string;
      category: CourseFaqCategoryAdmin;
      sortOrder: number;
      published: boolean;
    }>,
  ): Promise<AdminCourseFaq> => {
    const response = await api.patch(`/courses/${courseId}/faqs/${faqId}`, body);
    return response.data;
  },

  deleteFaq: async (courseId: string, faqId: string): Promise<void> => {
    await api.delete(`/courses/${courseId}/faqs/${faqId}`);
  },

  getSatisfactionStoriesAdmin: async (courseId: string): Promise<AdminCourseSatisfactionStory[]> => {
    const response = await api.get(`/courses/${courseId}/satisfaction-stories`);
    return response.data;
  },

  createSatisfactionStory: async (
    courseId: string,
    body: {
      firstName: string;
      lastName: string;
      jobTitle?: string;
      ratingStars?: number;
      coverUrl?: string;
      cardText: string;
      aparatVideoUrl?: string;
      sortOrder?: number;
      published?: boolean;
    },
  ): Promise<AdminCourseSatisfactionStory> => {
    const response = await api.post(`/courses/${courseId}/satisfaction-stories`, body);
    return response.data;
  },

  patchSatisfactionStory: async (
    courseId: string,
    storyId: string,
    body: Partial<{
      firstName: string;
      lastName: string;
      jobTitle: string;
      ratingStars: number;
      coverUrl: string;
      cardText: string;
      aparatVideoUrl: string;
      sortOrder: number;
      published: boolean;
    }>,
  ): Promise<AdminCourseSatisfactionStory> => {
    const response = await api.patch(`/courses/${courseId}/satisfaction-stories/${storyId}`, body);
    return response.data;
  },

  uploadSatisfactionStoryCover: async (
    courseId: string,
    storyId: string,
    file: File,
  ): Promise<AdminCourseSatisfactionStory> => {
    const fd = new FormData();
    fd.append('cover', file);
    const response = await api.patch(`/courses/${courseId}/satisfaction-stories/${storyId}/cover`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteSatisfactionStory: async (courseId: string, storyId: string): Promise<void> => {
    await api.delete(`/courses/${courseId}/satisfaction-stories/${storyId}`);
  },
};

export const videosService = {
  getAll: async (courseId?: string): Promise<Video[]> => {
    const url = courseId ? `/videos?courseId=${courseId}` : '/videos';
    const response = await api.get(url);
    return response.data;
  },

  getById: async (id: string): Promise<Video> => {
    const response = await api.get(`/videos/${id}`);
    return response.data;
  },

  create: async (data: Omit<Video, 'id' | 'createdAt' | 'updatedAt' | 'course'>): Promise<Video> => {
    const response = await api.post('/videos', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Video>): Promise<Video> => {
    const response = await api.patch(`/videos/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/videos/${id}`);
  },

  getStreamUrl: async (id: string) => {
    const response = await api.get(`/videos/${id}/stream-url`);
    return response.data;
  },

  grantAccess: async (videoId: string, userId: string) => {
    const response = await api.post(`/videos/${videoId}/access`, { userId });
    return response.data;
  },

  revokeAccess: async (videoId: string, userId: string) => {
    await api.delete(`/videos/${videoId}/access/${userId}`);
  },

  getAccessList: async (videoId: string) => {
    const response = await api.get(`/videos/${videoId}/access`);
    return response.data;
  },

  getUserAccessibleVideos: async () => {
    const response = await api.get('/videos/my-videos');
    return response.data;
  },

  bulkUpdate: async (videoIds: string[], data: Partial<Video>) => {
    const response = await api.patch('/videos/bulk', { videoIds, data });
    return response.data;
  },

  bulkDelete: async (videoIds: string[]) => {
    const response = await api.delete('/videos/bulk', { data: { videoIds } });
    return response.data;
  },

  publish: async (id: string) => {
    const response = await api.patch(`/videos/${id}/publish`);
    return response.data;
  },

  unpublish: async (id: string) => {
    const response = await api.patch(`/videos/${id}/unpublish`);
    return response.data;
  },

  search: async (query: string, filters?: any) => {
    const response = await api.get('/videos/search', { 
      params: { q: query, ...filters } 
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/videos/stats');
    return response.data;
  },

  exportVideos: async (format: 'csv' | 'excel' = 'csv') => {
    const response = await api.get(`/videos/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export const audiosService = {
  getAll: async (courseId?: string): Promise<Audio[]> => {
    const url = courseId ? `/audios?courseId=${courseId}` : '/audios';
    const response = await api.get(url);
    return response.data;
  },

  getById: async (id: string): Promise<Audio> => {
    const response = await api.get(`/audios/${id}`);
    return response.data;
  },

  create: async (data: Omit<Audio, 'id' | 'createdAt' | 'updatedAt' | 'course'>): Promise<Audio> => {
    const response = await api.post('/audios', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Audio>): Promise<Audio> => {
    const response = await api.patch(`/audios/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/audios/${id}`);
  },

  getStreamUrl: async (id: string) => {
    const response = await api.get(`/audios/${id}/stream-url`);
    return response.data;
  },

  grantAccess: async (audioId: string, userId: string) => {
    const response = await api.post(`/audios/${audioId}/access`, { userId });
    return response.data;
  },

  revokeAccess: async (audioId: string, userId: string) => {
    await api.delete(`/audios/${audioId}/access/${userId}`);
  },

  getAccessList: async (audioId: string) => {
    const response = await api.get(`/audios/${audioId}/access`);
    return response.data;
  },

  getUserAccessibleAudios: async () => {
    const response = await api.get('/audios/my-audios');
    return response.data;
  },

  bulkUpdate: async (audioIds: string[], data: Partial<Audio>) => {
    const response = await api.patch('/audios/bulk', { audioIds, data });
    return response.data;
  },

  bulkDelete: async (audioIds: string[]) => {
    const response = await api.delete('/audios/bulk', { data: { audioIds } });
    return response.data;
  },

  publish: async (id: string) => {
    const response = await api.patch(`/audios/${id}/publish`);
    return response.data;
  },

  unpublish: async (id: string) => {
    const response = await api.patch(`/audios/${id}/unpublish`);
    return response.data;
  },

  search: async (query: string, filters?: any) => {
    const response = await api.get('/audios/search', { 
      params: { q: query, ...filters } 
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/audios/stats');
    return response.data;
  },

  exportAudios: async (format: 'csv' | 'excel' = 'csv') => {
    const response = await api.get(`/audios/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export const workshopsService = {
  getAll: async (): Promise<Workshop[]> => {
    const response = await api.get('/workshops');
    return response.data;
  },

  getActive: async (): Promise<Workshop[]> => {
    try {
      const response = await api.get('/workshops/active');
      // Handle 304 Not Modified and ensure we return an array
      if (response.status === 304 && !response.data) {
        return [];
      }
      // Ensure response.data is an array
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error: any) {
      // If it's a 304 error, return empty array instead of throwing
      if (error.response?.status === 304) {
        return [];
      }
      throw error;
    }
  },

  getById: async (id: string): Promise<Workshop> => {
    const response = await api.get(`/workshops/${id}`);
    return response.data;
  },

  create: async (data: Omit<Workshop, 'id' | 'createdAt' | 'updatedAt' | 'creator' | 'participants'>): Promise<Workshop> => {
    const response = await api.post('/workshops', data);
    return response.data;
  },

  createWithThumbnail: async (formData: FormData): Promise<Workshop> => {
    const response = await api.post('/workshops', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود تصویر/فایل کارگاه ممکن است بیش از ۳۰ ثانیه طول بکشد
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Workshop>): Promise<Workshop> => {
    const response = await api.patch(`/workshops/${id}`, data);
    return response.data;
  },

  updateWithThumbnail: async (id: string, formData: FormData): Promise<Workshop> => {
    const response = await api.patch(`/workshops/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود تصویر/فایل کارگاه ممکن است بیش از ۳۰ ثانیه طول بکشد
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/workshops/${id}`);
  },

  getParticipants: async (workshopId: string): Promise<WorkshopParticipant[]> => {
    const response = await api.get(`/workshops/${workshopId}/participants`);
    return response.data;
  },

  addParticipant: async (workshopId: string, data: Omit<WorkshopParticipant, 'id' | 'createdAt' | 'updatedAt' | 'workshop' | 'creator'>): Promise<WorkshopParticipant> => {
    const response = await api.post(`/workshops/${workshopId}/participants`, data);
    return response.data;
  },

  updateParticipant: async (workshopId: string, participantId: string, data: Partial<WorkshopParticipant>): Promise<WorkshopParticipant> => {
    const response = await api.patch(`/workshops/${workshopId}/participants/${participantId}`, data);
    return response.data;
  },

  deleteParticipant: async (workshopId: string, participantId: string): Promise<void> => {
    await api.delete(`/workshops/${workshopId}/participants/${participantId}`);
  },

  generatePaymentLink: async (workshopId: string, participantId: string): Promise<{ paymentLink: string }> => {
    const response = await api.post(`/workshops/${workshopId}/participants/${participantId}/payment-link`);
    return response.data;
  },

  generateInvitationCard: async (workshopId: string, participantId: string): Promise<{ invitationCard: string }> => {
    const response = await api.post(`/workshops/${workshopId}/participants/${participantId}/invitation-card`);
    return response.data;
  },

  getSalesPersonAccessible: async (): Promise<Workshop[]> => {
    const response = await api.get('/workshops/sales-person/accessible');
    return response.data;
  },

  getSalesManagerWorkshops: async (): Promise<Workshop[]> => {
    const response = await api.get('/workshops/sales-manager/my-workshops');
    return response.data;
  },

  getWorkshopSalesPersonAccess: async (workshopId: string): Promise<any[]> => {
    const response = await api.get(`/workshops/${workshopId}/sales-person-access`);
    return response.data;
  },

  grantSalesPersonAccess: async (workshopId: string, salesPersonId: string): Promise<any> => {
    const response = await api.post(`/workshops/${workshopId}/sales-person-access`, { salesPersonId });
    return response.data;
  },

  revokeSalesPersonAccess: async (workshopId: string, salesPersonId: string): Promise<void> => {
    await api.delete(`/workshops/${workshopId}/sales-person-access/${salesPersonId}`);
  },

  bulkUpdate: async (workshopIds: string[], data: Partial<Workshop>) => {
    const response = await api.patch('/workshops/bulk', { workshopIds, data });
    return response.data;
  },

  bulkDelete: async (workshopIds: string[]) => {
    const response = await api.delete('/workshops/bulk', { data: { workshopIds } });
    return response.data;
  },

  activate: async (id: string) => {
    const response = await api.patch(`/workshops/${id}/activate`);
    return response.data;
  },

  deactivate: async (id: string) => {
    const response = await api.patch(`/workshops/${id}/deactivate`);
    return response.data;
  },

  search: async (query: string, filters?: any) => {
    const response = await api.get('/workshops/search', { 
      params: { q: query, ...filters } 
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/workshops/stats');
    return response.data;
  },

  exportWorkshops: async (format: 'csv' | 'excel' = 'csv') => {
    const response = await api.get(`/workshops/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getWorkshopAnalytics: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/analytics`);
    return response.data;
  },

  getParticipantAnalytics: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/participants/analytics`);
    return response.data;
  },

  getUserParticipatedWorkshops: async (): Promise<Workshop[]> => {
    const response = await api.get('/workshops/user/participated');
    return response.data;
  },

  uploadVideos: async (workshopId: string, files: File[], onProgress?: (progressEvent: any) => void) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('videos', file);
    });
    
    const response = await api.patch(`/workshops/${workshopId}/videos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود ویدیو ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  uploadAudios: async (workshopId: string, files: File[], onProgress?: (progressEvent: any) => void) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('audios', file);
    });

    const response = await api.patch(`/workshops/${workshopId}/audios`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // آپلود صوت ممکن است بیش از ۳۰ ثانیه طول بکشد
      onUploadProgress: onProgress,
    });
    return response.data;
  },
};

export const salesService = {
  getSalesReport: async (period?: string) => {
    const url = period ? `/sales/report?period=${period}` : '/sales/report';
    const response = await api.get(url);
    return response.data;
  },

  getSalesTeam: async () => {
    const response = await api.get('/sales/team');
    return response.data;
  },

  getMySalesReport: async (period?: string) => {
    const url = period ? `/sales/my-report?period=${period}` : '/sales/my-report';
    const response = await api.get(url);
    return response.data;
  },

  getSalesAnalytics: async (filters?: any) => {
    const response = await api.get('/sales/analytics', { params: filters });
    return response.data;
  },

  getSalesTrends: async (period: string = 'monthly') => {
    const response = await api.get(`/sales/trends?period=${period}`);
    return response.data;
  },

  getTopPerformers: async (period?: string) => {
    const url = period ? `/sales/top-performers?period=${period}` : '/sales/top-performers';
    const response = await api.get(url);
    return response.data;
  },

  getSalesByRegion: async () => {
    const response = await api.get('/sales/by-region');
    return response.data;
  },

  getSalesByProduct: async () => {
    const response = await api.get('/sales/by-product');
    return response.data;
  },

  getCommissionReport: async (period?: string) => {
    const url = period ? `/sales/commission?period=${period}` : '/sales/commission';
    const response = await api.get(url);
    return response.data;
  },

  calculateCommission: async (salesPersonId: string, amount: number) => {
    const response = await api.post('/sales/calculate-commission', { salesPersonId, amount });
    return response.data;
  },

  getSalesTargets: async () => {
    const response = await api.get('/sales/targets');
    return response.data;
  },

  setSalesTarget: async (salesPersonId: string, target: number, period: string) => {
    const response = await api.post('/sales/targets', { salesPersonId, target, period });
    return response.data;
  },

  updateSalesTarget: async (targetId: string, target: number) => {
    const response = await api.patch(`/sales/targets/${targetId}`, { target });
    return response.data;
  },

  exportSalesReport: async (format: 'csv' | 'excel' = 'csv', filters?: any) => {
    const response = await api.get(`/sales/export?format=${format}`, {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  getSalesForecast: async (period: string = 'monthly') => {
    const response = await api.get(`/sales/forecast?period=${period}`);
    return response.data;
  },

  getCustomerAnalytics: async () => {
    const response = await api.get('/sales/customer-analytics');
    return response.data;
  },

  getCustomerLifetimeValue: async () => {
    const response = await api.get('/sales/customer-lifetime-value');
    return response.data;
  },
};

export const uploadsService = {
  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/uploads/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadVideo: async (formData: FormData, onProgress?: (progressEvent: any) => void) => {
    const response = await api.post('/uploads/video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });
    return response.data;
  },

  uploadAudio: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/uploads/audio', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadDocument: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/uploads/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadMultiple: async (files: File[], type: 'image' | 'video' | 'audio' | 'document' = 'image') => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    
    const response = await api.post(`/uploads/multiple/${type}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteFile: async (fileId: string) => {
    await api.delete(`/uploads/${fileId}`);
  },

  getFileInfo: async (fileId: string) => {
    const response = await api.get(`/uploads/${fileId}`);
    return response.data;
  },

  validateFile: async (file: File, type: 'image' | 'video' | 'audio' | 'document') => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/uploads/validate/${type}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getUploadStats: async () => {
    const response = await api.get('/uploads/stats');
    return response.data;
  },

  cleanupUnusedFiles: async () => {
    const response = await api.post('/uploads/cleanup');
    return response.data;
  },
};

export const systemService = {
  getHealth: async () => {
    const response = await api.get('/health');
    return response.data;
  },

  getDashboardAnalytics: async (days?: number) => {
    const response = await api.get('/admin/stats/analytics', {
      params: days != null && Number.isFinite(days) ? { days } : {},
    });
    return response.data;
  },

  getStats: async () => {
    // This might be provided by a dashboard service, but keeping it here for now
    const response = await api.get('/admin/stats');
    return response.data;
  },

  backupDatabase: async () => {
    const response = await api.get('/admin/backup', {
      responseType: 'blob',
    });
    return response.data;
  },

  clearCache: async () => {
    const response = await api.post('/admin/clear-cache');
    return response.data;
  },

  getLogs: async (filters?: any) => {
    const response = await api.get('/logs', { params: filters });
    return response.data;
  },
};

export const salesTeamsService = {
  getAll: async () => {
    const response = await api.get('/sales-teams');
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/sales-teams/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    managerId: string;
    description?: string;
    salesPersonIds?: string[];
  }) => {
    const response = await api.post('/sales-teams', data);
    return response.data;
  },

  update: async (id: string, data: {
    name?: string;
    managerId?: string;
    description?: string;
    isActive?: boolean;
  }) => {
    const response = await api.patch(`/sales-teams/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/sales-teams/${id}`);
    return response.data;
  },

  addMember: async (teamId: string, salesPersonId: string) => {
    const response = await api.post(`/sales-teams/${teamId}/members`, {
      salesPersonId,
    });
    return response.data;
  },

  removeMember: async (teamId: string, salesPersonId: string) => {
    const response = await api.delete(`/sales-teams/${teamId}/members`, {
      data: { salesPersonId },
    });
    return response.data;
  },

  getAvailableSalesPersons: async () => {
    const response = await api.get('/sales-teams/available-sales-persons');
    return response.data;
  },

  getSalesManagers: async () => {
    const response = await api.get('/sales-teams/sales-managers');
    return response.data;
  },
};

export const logsService = {
  getLogs: async (filters?: {
    level?: 'LOG' | 'ERROR' | 'WARN' | 'DEBUG' | 'VERBOSE';
    context?: string;
    url?: string;
    statusCode?: number;
    search?: string;
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await api.get('/logs', { params: filters });
    return response.data;
  },

  getLogStats: async () => {
    const response = await api.get('/logs/stats');
    return response.data;
  },

  deleteOldLogs: async (days: number) => {
    const response = await api.delete(`/logs/old/${days}`);
    return response.data;
  },

  deleteAllLogs: async () => {
    const response = await api.delete('/logs/all');
    return response.data;
  },
};

export const adminService = {
  createDatabaseBackup: async (): Promise<Blob> => {
    const response = await api.get('/admin/backup', {
      responseType: 'blob',
    });
    return response.data;
  },

  creditUserWallet: async (userId: string, data: { amount: number; description?: string }) => {
    const response = await api.post(`/admin/users/${userId}/wallet/credit`, data);
    return response.data as {
      success: boolean;
      message: string;
      walletBalance: number;
      transactionId: string;
    };
  },

  debitUserWallet: async (userId: string, data: { amount: number; description?: string }) => {
    const response = await api.post(`/admin/users/${userId}/wallet/debit`, data);
    return response.data as {
      success: boolean;
      message: string;
      walletBalance: number;
      transactionId: string;
    };
  },
};

export const paymentsService = {
  createPaymentLink: async (data: {
    customerName: string;
    customerMobile: string;
    amount: number;
    description?: string;
    workshopTitle?: string;
    isAggregate?: boolean;
    aggregateCount?: number;
  }) => {
    const response = await api.post('/payments/links', data);
    return response.data;
  },

  getCustomerPaymentHistory: async (phone: string) => {
    const response = await api.get(`/payments/customer-history/${phone}`);
    return response.data;
  },

  getPaymentLinks: async () => {
    const response = await api.get('/payments/links');
    return response.data;
  },

  getCustomerPaymentLinks: async (phone: string) => {
    const response = await api.get(`/payments/links/customer/${phone}`);
    return response.data;
  },

  getAllPaymentLinks: async () => {
    const response = await api.get('/payments/links');
    return response.data;
  },

  togglePaymentLink: async (linkId: string) => {
    const response = await api.patch(`/payments/links/${linkId}/toggle`);
    return response.data;
  },

  getPaymentLinkByCode: async (linkCode: string) => {
    const response = await api.get(`/payments/pay/${linkCode}`);
    return response.data;
  },
  getAllInvoices: async (params?: { page?: number; limit?: number; status?: string; type?: string; userId?: string }) => {
    const response = await api.get('/payments/invoices/all', { params });
    return response.data;
  },

  getAllCourseInvoices: async (params?: { page?: number; limit?: number; status?: string; userId?: string }) => {
    const response = await api.get('/payments/invoices/course-invoices', { params });
    return response.data;
  },

  getPaymentLinkInvoices: async (params?: { page?: number; limit?: number; status?: string; salesPersonId?: string }) => {
    const response = await api.get('/payments/invoices/payment-links', { params });
    return response.data;
  },

  getSalesPersonsPaymentStats: async () => {
    const response = await api.get('/payments/salespersons/stats');
    return response.data;
  },

  getInvoiceById: async (invoiceId: string) => {
    const response = await api.get(`/payments/invoices/${invoiceId}`);
    return response.data;
  },

  getCourseInvoices: async (courseId: string) => {
    const response = await api.get(`/payments/course/${courseId}/invoices`);
    return response.data;
  },

  getPaymentLinksReport: async (params?: string) => {
    const url = params ? `/payments/reports/payment-links?${params}` : '/payments/reports/payment-links';
    const response = await api.get(url);
    return response.data;
  },

  getTeamPaymentLinks: async (): Promise<Array<{
    id: string;
    linkCode: string;
    customerName?: string;
    customerPhone?: string;
    amount: number;
    description?: string;
    status: string;
    sellerName: string;
    createdAt: string;
  }>> => {
    const response = await api.get('/payments/team/links');
    return response.data;
  },

  getAdminWalletTopupRequests: async (params?: { status?: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CLOSED'; search?: string }) => {
    const response = await api.get('/payments/admin/wallet/topup-requests', { params });
    return response.data;
  },

  approveWalletTopupRequest: async (id: string, data?: { amount?: number; description?: string }) => {
    const response = await api.patch(`/payments/admin/wallet/topup-requests/${id}/approve`, data || {});
    return response.data;
  },

  rejectWalletTopupRequest: async (id: string, data?: { reason?: string }) => {
    const response = await api.patch(`/payments/admin/wallet/topup-requests/${id}/reject`, data || {});
    return response.data;
  },

  getUserWalletOverview: async (userId: string, limit: number = 20) => {
    const response = await api.get(`/payments/admin/wallet/users/${userId}/overview`, {
      params: { limit },
    });
    return response.data as {
      user: {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        username: string;
        phone?: string | null;
        email?: string | null;
      };
      balance: number;
      transactions: any[];
    };
  },

  getAdminWalletTransactions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
    userId?: string;
  }) => {
    const response = await api.get('/payments/admin/wallet/transactions', { params });
    return response.data as {
      data: any[];
      meta: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    };
  },
};

export const settingsService = {
  getSettings: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  updateSettings: async (settings: any) => {
    const response = await api.patch('/settings', settings);
    return response.data;
  },
};

export const uploadCenterService = {
  getAllFiles: async () => {
    const response = await api.get('/uploads');
    return response.data;
  },

  getVideos: async () => {
    const files = await api.get('/uploads');
    return files.data.filter((f: any) => f.type === 'video');
  },

  getAudios: async () => {
    const files = await api.get('/uploads');
    return files.data.filter((f: any) => f.type === 'audio');
  },

  deleteFile: async (filename: string, force: boolean = false) => {
    const url = `/uploads/${encodeURIComponent(filename)}${force ? '?force=true' : ''}`;
    await api.delete(url);
  },

  assignFileToCourse: async (filename: string, courseId: string, title?: string, description?: string, forceReassign: boolean = false) => {
    const response = await api.post(`/uploads/${encodeURIComponent(filename)}/assign`, {
      courseId,
      title,
      description,
      forceReassign
    });
    return response.data;
  },

  assignVideoToCourse: async (filename: string, courseId: string) => {
    const response = await api.post(`/uploads/${encodeURIComponent(filename)}/assign-video`, {
      courseId
    });
    return response.data;
  },

  downloadFile: async (filename: string) => {
    return `${API_ORIGIN}/uploads/${encodeURIComponent(filename)}`;
  },

  streamFile: async (filename: string) => {
    console.log(filename)
    return `${API_ORIGIN}/uploads/stream/${encodeURIComponent(filename)}`;
  }
};

export type AdminCoupon = {
  id: string;
  code: string;
  description?: string | null;
  discountKind: 'PERCENT' | 'FIXED_TOMAN';
  percentOff?: string | number | null;
  amountOffToman?: string | number | null;
  maxUsesTotal?: number | null;
  usesCount: number;
  perUserPerCourseLimit: number;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive: boolean;
  applicableCourseIds: string[];
  excludedCourseIds?: string[];
  minOrderToman?: string | number | null;
  minCartLinesCount?: number | null;
  minCartSubtotalToman?: string | number | null;
  newUsersRegisteredAfter?: string | null;
  campaignLabel?: string | null;
  maxDiscountToman?: string | number | null;
  firstPaidPurchaseOnly?: boolean;
  maxUsesPerUser?: number | null;
  createdAt: string;
  updatedAt: string;
};

export type AdminCouponReportSummary = {
  lifetimeRedemptions: number;
  lifetimeDiscountTomanSum: number;
  recent30dRedemptions: number;
  recent30dDiscountTomanSum: number;
  couponsConfigured: number;
  couponsActive: number;
  topByVolume: Array<{
    couponId: string;
    code: string;
    campaignLabel: string | null;
    redemptionsCount: number;
    discountTomanSum: number;
  }>;
};

export type AdminCouponReportDetailed = {
  days: number;
  windowStartsAt: string;
  windowEndsAt: string;
  windowRedemptions: number;
  windowDiscountTomanSum: number;
  avgDiscountPerRedemptionWindow: number;
  lifetimeRedemptions: number;
  lifetimeDiscountTomanSum: number;
  couponsConfigured: number;
  couponsActive: number;
  dailyTrend: Array<{
    date: string;
    redemptionCount: number;
    discountTomanSum: number;
  }>;
  topByRedemptions: Array<{
    couponId: string;
    code: string;
    campaignLabel: string | null;
    redemptionsCount: number;
    discountTomanSum: number;
  }>;
  topByDiscountSum: Array<{
    couponId: string;
    code: string;
    campaignLabel: string | null;
    redemptionsCount: number;
    discountTomanSum: number;
  }>;
  byCampaign: Array<{
    campaignLabel: string | null;
    redemptionsCount: number;
    discountTomanSum: number;
  }>;
  couponMatrix: Array<{
    couponId: string;
    code: string;
    campaignLabel: string | null;
    isActive: boolean;
    usesCountLifetime: number;
    maxUsesTotal: number | null;
    discountKind: string;
    createdAt: string;
    windowRedemptions: number;
    windowDiscountTomanSum: number;
  }>;
};

export type AdminCouponRedemption = {
  id: string;
  couponId: string;
  userId: string;
  courseId: string;
  invoiceId: string | null;
  discountToman: string | number;
  createdAt: string;
  user: {
    id: string;
    username?: string;
    phone?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    createdAt?: string;
  };
  course: { id: string; title: string; slug?: string };
  invoice: {
    id: string;
    invoiceNumber: string;
    amount: string | number;
    status: string;
    paidAt: string | null;
    couponCodeSnapshot: string | null;
    courseSubtotalToman: string | number | null;
    couponDiscountToman: string | number | null;
    type: string;
  } | null;
};

export const couponsAdminService = {
  reportSummary: async (): Promise<AdminCouponReportSummary> => {
    const response = await api.get('/admin/coupons/report/summary');
    return response.data as AdminCouponReportSummary;
  },
  reportDetailed: async (days = 30): Promise<AdminCouponReportDetailed> => {
    const response = await api.get('/admin/coupons/report/detailed', { params: { days } });
    return response.data as AdminCouponReportDetailed;
  },
  reconcileRedemptionsFromInvoices: async (
    max = 12000,
  ): Promise<{ examined: number; created: number; skippedHadRow: number }> => {
    const response = await api.post('/admin/coupons/actions/reconcile', { max });
    return response.data as { examined: number; created: number; skippedHadRow: number };
  },
  list: async (): Promise<AdminCoupon[]> => {
    const response = await api.get('/admin/coupons');
    return response.data as AdminCoupon[];
  },
  redemptions: async (id: string): Promise<AdminCouponRedemption[]> => {
    const response = await api.get(`/admin/coupons/${id}/redemptions`);
    return response.data as AdminCouponRedemption[];
  },
  duplicate: async (id: string, body?: { code?: string }): Promise<AdminCoupon> => {
    const response = await api.post(`/admin/coupons/${id}/duplicate`, body ?? {});
    return response.data as AdminCoupon;
  },
  create: async (payload: Record<string, unknown>) => {
    const response = await api.post('/admin/coupons', payload);
    return response.data as AdminCoupon;
  },
  update: async (id: string, payload: Record<string, unknown>) => {
    const response = await api.patch(`/admin/coupons/${id}`, payload);
    return response.data as AdminCoupon;
  },
  remove: async (id: string) => {
    await api.delete(`/admin/coupons/${id}`);
  },
};

export const leadsService = {
  getAdminLeads: async (params?: {
    page?: number;
    limit?: number;
    status?: LeadStatus;
    search?: string;
    source?: string;
    hasNote?: boolean;
    responsibleSellerId?: string;
    createdFrom?: string;
    createdTo?: string;
  }) => {
    const response = await api.get('/leads/admin', { params });
    return response.data as {
      data: Lead[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    };
  },

  updateAdminLead: async (
    id: string,
    payload: { status?: LeadStatus; note?: string; responsibleSellerId?: string | null },
  ) => {
    const response = await api.patch(`/leads/admin/${id}`, payload);
    return response.data as Lead;
  },

  deleteAdminLead: async (id: string) => {
    const response = await api.delete(`/leads/admin/${id}`);
    return response.data as { message: string };
  },

  getAdminLeadStats: async () => {
    const response = await api.get('/leads/admin/stats');
    return response.data as LeadStats;
  },

  exportAdminLeads: async (params?: {
    status?: string;
    search?: string;
    source?: string;
    createdFrom?: string;
    createdTo?: string;
  }) => {
    const response = await api.get('/leads/admin/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};


// ─── پرسشنامهٔ مسیر رشد ───────────────────────────────────────────────────────

export interface AdminAssessmentWorkshop {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  linkedWorkshopId?: string | null;
  _count?: { sections: number; attempts: number };
}

export interface AdminAssessmentQuestion {
  id: string;
  sectionId: string;
  text: string;
  sortOrder: number;
  reverseScored: boolean;
}

export interface AdminAssessmentSection {
  id: string;
  assessmentId: string;
  title: string;
  description?: string | null;
  sortOrder: number;
  courseId?: string | null;
  workshopId?: string | null;
  published: boolean;
  /** false = پرسیده می‌شود اما در نتیجه/امتیاز نمی‌آید */
  scored?: boolean;
  /** متن تحلیل سه سطح — فقط پرسشنامهٔ حالت ANALYSIS */
  analysisHigh?: string | null;
  analysisMedium?: string | null;
  analysisLow?: string | null;
  questions: AdminAssessmentQuestion[];
  course?: { id: string; title: string } | null;
  workshop?: { id: string; title: string } | null;
}

export type AdminAssessmentResultMode = 'COURSE_PRIORITY' | 'ANALYSIS';
export type AdminAssessmentScaleType = 'LIKERT_5' | 'YES_NO';

export interface AdminAssessment {
  id: string;
  title: string;
  accessMode?: 'PUBLIC' | 'INVITE_ONLY';
  /** COURSE_PRIORITY = مسیر رشد (اولویت دوره‌ها)؛ ANALYSIS = تحلیل متنی + CTA */
  resultMode?: AdminAssessmentResultMode;
  /** LIKERT_5 = پنج گزینه؛ YES_NO = بله/خیر */
  scaleType?: AdminAssessmentScaleType;
  /** پاسخ‌ها به نام عنوان کارگاه ثبت شوند (انتخاب کارگاه هنگام شروع + تنها کارگاه فعال) */
  useWorkshops?: boolean;
  ctaTitle?: string | null;
  ctaText?: string | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  slug: string;
  description?: string | null;
  introText?: string | null;
  published: boolean;
  allowRetake: boolean;
  optionLabels: string[];
  sections?: AdminAssessmentSection[];
  _count?: { sections: number; attempts: number };
}

export interface AdminAssessmentAttemptRow {
  id: string;
  completedAt: string | null;
  user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null; email?: string | null };
  workshopTitle: string | null;
  topPriorities: Array<{ title: string; score25: number }>;
}

export interface AdminAssessmentAggregateRow {
  sectionId: string;
  title: string;
  course: { id: string; title: string } | null;
  averageScore25: number;
  responses: number;
}

export const assessmentsService = {
  // کارگاه‌ها
  /** با `assessmentId`، شمارندهٔ پاسخ هر کارگاه فقط پاسخ‌های تکمیل‌شدهٔ همان پرسشنامه است */
  listWorkshops: async (assessmentId?: string): Promise<AdminAssessmentWorkshop[]> => {
    const response = await api.get('/admin/assessments/workshops', {
      params: assessmentId ? { assessmentId } : undefined,
    });
    return response.data;
  },
  listLinkableWorkshops: async (): Promise<Array<{ id: string; title: string; date: string }>> => {
    const response = await api.get('/admin/assessments/workshops/linkable');
    return response.data;
  },
  createWorkshop: async (data: Partial<AdminAssessmentWorkshop>): Promise<AdminAssessmentWorkshop> => {
    const response = await api.post('/admin/assessments/workshops', data);
    return response.data;
  },
  updateWorkshop: async (id: string, data: Partial<AdminAssessmentWorkshop>): Promise<AdminAssessmentWorkshop> => {
    const response = await api.patch(`/admin/assessments/workshops/${id}`, data);
    return response.data;
  },
  deleteWorkshop: async (id: string): Promise<void> => {
    await api.delete(`/admin/assessments/workshops/${id}`);
  },

  // پرسشنامه
  list: async (): Promise<AdminAssessment[]> => {
    const response = await api.get('/admin/assessments');
    return response.data;
  },
  getById: async (id: string): Promise<AdminAssessment> => {
    const response = await api.get(`/admin/assessments/${id}`);
    return response.data;
  },
  create: async (data: Partial<AdminAssessment>): Promise<AdminAssessment> => {
    const response = await api.post('/admin/assessments', data);
    return response.data;
  },
  update: async (id: string, data: Partial<AdminAssessment>): Promise<AdminAssessment> => {
    const response = await api.patch(`/admin/assessments/${id}`, data);
    return response.data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/admin/assessments/${id}`);
  },

  // بخش‌ها
  createSection: async (assessmentId: string, data: Partial<AdminAssessmentSection>): Promise<AdminAssessmentSection> => {
    const response = await api.post(`/admin/assessments/${assessmentId}/sections`, data);
    return response.data;
  },
  updateSection: async (sectionId: string, data: Partial<AdminAssessmentSection>): Promise<AdminAssessmentSection> => {
    const response = await api.patch(`/admin/assessments/sections/${sectionId}`, data);
    return response.data;
  },
  deleteSection: async (sectionId: string): Promise<void> => {
    await api.delete(`/admin/assessments/sections/${sectionId}`);
  },
  reorderSections: async (assessmentId: string, ids: string[]): Promise<void> => {
    await api.patch(`/admin/assessments/${assessmentId}/sections/reorder`, { ids });
  },

  // سوال‌ها
  createQuestion: async (sectionId: string, data: Partial<AdminAssessmentQuestion>): Promise<AdminAssessmentQuestion> => {
    const response = await api.post(`/admin/assessments/sections/${sectionId}/questions`, data);
    return response.data;
  },
  updateQuestion: async (questionId: string, data: Partial<AdminAssessmentQuestion>): Promise<AdminAssessmentQuestion> => {
    const response = await api.patch(`/admin/assessments/questions/${questionId}`, data);
    return response.data;
  },
  deleteQuestion: async (questionId: string): Promise<void> => {
    await api.delete(`/admin/assessments/questions/${questionId}`);
  },
  reorderQuestions: async (sectionId: string, ids: string[]): Promise<void> => {
    await api.patch(`/admin/assessments/sections/${sectionId}/questions/reorder`, { ids });
  },

  // گزارش‌ها
  listAttempts: async (
    assessmentId: string,
    params?: { workshopId?: string; take?: number; skip?: number; search?: string },
  ): Promise<{ total: number; attempts: AdminAssessmentAttemptRow[] }> => {
    const response = await api.get(`/admin/assessments/${assessmentId}/attempts`, { params });
    return response.data;
  },
  getAggregate: async (
    assessmentId: string,
    workshopId?: string,
  ): Promise<AdminAssessmentAggregateRow[]> => {
    const response = await api.get(`/admin/assessments/${assessmentId}/aggregate`, {
      params: workshopId ? { workshopId } : undefined,
    });
    return response.data;
  },
};

export interface AdminAssessmentInvite {
  id: string;
  code: string;
  label?: string | null;
  phone?: string | null;
  maxUses: number;
  usedCount: number;
  isActive: boolean;
  expiresAt?: string | null;
  createdAt: string;
  workshop?: { id: string; title: string } | null;
  claims: Array<{
    userId: string;
    claimedAt: string;
    name: string | null;
    phone: string | null;
    completed: boolean;
  }>;
}

export interface AdminAssessmentSectionStat {
  sectionId: string;
  title: string;
  course: { id: string; title: string; slug: string } | null;
  responses: number;
  average: number;
  median: number;
  min: number;
  max: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  topThreeCount: number;
}

export interface AdminAssessmentAnalytics {
  resultMode?: AdminAssessmentResultMode;
  kpis: {
    totalCompleted: number;
    uniqueRespondents: number;
    inProgress: number;
    invitedCount: number;
    completionRate: number | null;
    sectionsWithoutCourse: number;
    mostUrgent: string | null;
    /** پاسخ‌های تکمیل‌شده‌ای که به نام هیچ کارگاهی ثبت نشده‌اند (فقط بدون فیلتر کارگاه) */
    withoutWorkshopCount?: number;
  };
  sectionStats: AdminAssessmentSectionStat[];
  courseDemand: Array<{
    id: string;
    title: string;
    slug: string;
    topThree: number;
    highPriority: number;
  }>;
}

export interface AdminAttemptDetail {
  attemptId: string;
  completedAt: string | null;
  user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null; email?: string | null };
  workshop: { id: string; title: string } | null;
  resultMode?: AdminAssessmentResultMode;
  items: Array<{
    sectionId: string;
    title: string;
    course: { id: string; title: string } | null;
    score25: number;
    intensity?: number;
    rank: number;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
  }>;
}

export interface AdminAttemptReportQuestion {
  id: string;
  text: string;
  sortOrder: number;
  reverseScored: boolean;
  value: number | null;
  label: string | null;
  effectiveScore: number | null;
}

export interface AdminAttemptReportSection {
  sectionId: string;
  title: string;
  description: string | null;
  rank: number;
  rawScore: number;
  maxScore: number;
  score25: number;
  intensity?: number;
  /** تعداد «بله» — فقط مقیاس بله/خیر */
  agreeCount?: number | null;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  /** متن تحلیل سطح این محور — فقط پرسشنامهٔ حالت ANALYSIS */
  analysis?: string | null;
  cohortAverageScore25: number | null;
  course: { id: string; title: string } | null;
  questions: AdminAttemptReportQuestion[];
}

export interface AdminAttemptReport {
  attemptId: string;
  startedAt: string;
  completedAt: string | null;
  assessment: {
    id: string;
    title: string;
    description: string | null;
    optionLabels: string[];
    resultMode?: AdminAssessmentResultMode;
    scaleType?: AdminAssessmentScaleType;
  };
  user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null; email?: string | null };
  workshop: { id: string; title: string } | null;
  cohort: { respondentCount: number; scope: 'workshop' | 'all' };
  summary: {
    sectionCount: number;
    questionCount: number;
    answeredCount: number;
    highPriorityCount: number;
    mediumPriorityCount: number;
    lowPriorityCount: number;
    averageScore25: number;
    overallPercent: number;
  };
  sections: AdminAttemptReportSection[];
  /** بخش‌های بدون امتیاز (مثل «آمادگی برای تغییر») با پاسخ‌ها — برای تیم فروش */
  extraSections?: Array<{
    sectionId: string;
    title: string;
    questions: Array<{ id: string; text: string; sortOrder: number; value: number | null; label: string | null }>;
  }>;
}

export const assessmentInvitesService = {
  list: async (assessmentId: string, workshopId?: string): Promise<AdminAssessmentInvite[]> => {
    const response = await api.get(`/admin/assessments/${assessmentId}/invites`, {
      params: workshopId ? { workshopId } : undefined,
    });
    return response.data;
  },
  create: async (
    assessmentId: string,
    data: { workshopId?: string; phone?: string; label?: string; maxUses?: number; expiresAt?: string },
  ): Promise<AdminAssessmentInvite> => {
    const response = await api.post(`/admin/assessments/${assessmentId}/invites`, data);
    return response.data;
  },
  bulk: async (
    assessmentId: string,
    data: { workshopId?: string; phones?: string[]; count?: number; label?: string; maxUses?: number; expiresAt?: string },
  ): Promise<{ created: number }> => {
    const response = await api.post(`/admin/assessments/${assessmentId}/invites/bulk`, data);
    return response.data;
  },
  fromWorkshop: async (assessmentId: string, workshopId: string): Promise<{ created: number }> => {
    const response = await api.post(
      `/admin/assessments/${assessmentId}/invites/from-workshop/${workshopId}`,
    );
    return response.data;
  },
  update: async (
    inviteId: string,
    data: { label?: string; isActive?: boolean; maxUses?: number; workshopId?: string; expiresAt?: string },
  ): Promise<AdminAssessmentInvite> => {
    const response = await api.patch(`/admin/assessments/invites/${inviteId}`, data);
    return response.data;
  },
  remove: async (inviteId: string): Promise<void> => {
    await api.delete(`/admin/assessments/invites/${inviteId}`);
  },
};

export const assessmentAnalyticsService = {
  get: async (assessmentId: string, workshopId?: string): Promise<AdminAssessmentAnalytics> => {
    const response = await api.get(`/admin/assessments/${assessmentId}/analytics`, {
      params: workshopId ? { workshopId } : undefined,
    });
    return response.data;
  },
  attemptDetail: async (attemptId: string): Promise<AdminAttemptDetail> => {
    const response = await api.get(`/admin/assessments/attempts/${attemptId}/detail`);
    return response.data;
  },
  attemptReport: async (attemptId: string): Promise<AdminAttemptReport> => {
    const response = await api.get(`/admin/assessments/attempts/${attemptId}/report`);
    return response.data;
  },
  exportExcel: async (assessmentId: string, workshopId?: string): Promise<Blob> => {
    const response = await api.get(`/admin/assessments/${assessmentId}/attempts/export`, {
      params: workshopId ? { workshopId } : undefined,
      responseType: 'blob',
      timeout: 0, // ممکن است تعداد پاسخ‌دهندگان زیاد باشد
    });
    return response.data;
  },
};

// ─── پرونده‌های کسب‌وکار (6C) ─────────────────────────────────────────────────

export type BusinessBand = 'CRITICAL' | 'NEEDS_WORK' | 'RELIABLE' | 'STRENGTH';
export type BusinessCaseStatus = 'DRAFT' | 'SUBMITTED' | 'IN_REVIEW' | 'TRIAGED' | 'CLOSED';
export type BusinessFitDecision = 'FIT' | 'NEEDS_REVIEW' | 'NOT_FIT';

export interface BusinessCaseRow {
  id: string;
  status: BusinessCaseStatus;
  businessName?: string | null;
  industry?: string | null;
  submittedAt?: string | null;
  overallScore: number | null;
  band: BusinessBand | null;
  redFlagCount: number;
  stabilizeFirst: boolean;
  weakestAxis: { id: string; title: string; subtitle?: string | null } | null;
  fitDecision: BusinessFitDecision | null;
  user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null };
  mentor: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

export interface BusinessCaseDetail extends Omit<BusinessCaseRow, 'weakestAxis' | 'fitDecision'> {
  form: { id: string; title: string; slug: string };
  profile: Record<string, string | null>;
  bandLabel: string | null;
  direction: string | null;
  axisScores: Array<{
    axisId: string;
    key: string;
    title: string;
    subtitle?: string | null;
    total: number;
    average: number;
    band: BusinessBand;
    bandLabel: string;
    direction: string;
  }>;
  strongest: BusinessCaseDetail['axisScores'][number] | null;
  weakest: BusinessCaseDetail['axisScores'][number] | null;
  metrics: Array<{
    metricId: string;
    title: string;
    current?: string | null;
    target?: string | null;
    source?: string | null;
    notMeasured: boolean;
  }>;
  redFlags: Array<{
    flagId: string;
    text: string;
    actionHint?: string | null;
    value: boolean;
    note?: string | null;
  }>;
  priorities: Array<{
    id: string;
    rank: number;
    action: string;
    owner?: string | null;
    dueDate?: string | null;
    successMetric?: string | null;
  }>;
  review: {
    coreProblem?: string | null;
    rootCause?: string | null;
    costOfInaction?: string | null;
    ninetyDayOutcome?: string | null;
    fitDecision?: BusinessFitDecision | null;
    suggestedPath?: string | null;
    timeCommitment?: string | null;
    successCriteria?: string | null;
    meetingDate?: string | null;
  } | null;
  answers: Array<{
    statementId: string;
    axisId: string;
    text: string;
    score: number;
    evidence?: string | null;
  }>;
  notes: Array<{
    id: string;
    body: string;
    createdAt: string;
    author: { id: string; firstName?: string | null; lastName?: string | null };
  }>;
}

export const businessCasesService = {
  listMentors: async (): Promise<
    Array<{ id: string; firstName?: string | null; lastName?: string | null; phone?: string | null }>
  > => {
    const response = await api.get('/staff/business/mentors');
    return response.data;
  },
  list: async (params?: {
    status?: BusinessCaseStatus;
    mentorId?: string;
    search?: string;
    take?: number;
    skip?: number;
  }): Promise<{ total: number; cases: BusinessCaseRow[] }> => {
    const response = await api.get('/staff/business/cases', { params });
    return response.data;
  },
  get: async (caseId: string): Promise<BusinessCaseDetail> => {
    const response = await api.get(`/staff/business/cases/${caseId}`);
    return response.data;
  },
  assign: async (caseId: string, mentorId: string | null): Promise<void> => {
    await api.patch(`/staff/business/cases/${caseId}/mentor`, { mentorId: mentorId || '' });
  },
  saveReview: async (
    caseId: string,
    data: Record<string, string | undefined>,
  ): Promise<void> => {
    await api.patch(`/staff/business/cases/${caseId}/review`, data);
  },
  setPriorities: async (
    caseId: string,
    priorities: Array<{ action: string; owner?: string; dueDate?: string; successMetric?: string }>,
  ): Promise<void> => {
    await api.patch(`/staff/business/cases/${caseId}/priorities`, { priorities });
  },
  addNote: async (caseId: string, body: string): Promise<void> => {
    await api.post(`/staff/business/cases/${caseId}/notes`, { body });
  },
  deleteNote: async (noteId: string): Promise<void> => {
    await api.delete(`/staff/business/notes/${noteId}`);
  },
  /** خروجی اکسل کامل با همان فیلترهای صفحه — برای فرستادن به تیم منتورها */
  exportExcel: async (params?: {
    status?: BusinessCaseStatus;
    mentorId?: string;
    search?: string;
  }): Promise<Blob> => {
    const response = await api.get('/staff/business/cases/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  getForm: async (): Promise<any> => {
    const response = await api.get('/staff/business/form');
    return response.data;
  },
  updateForm: async (formId: string, data: any): Promise<any> => {
    const response = await api.patch(`/staff/business/form/${formId}`, data);
    return response.data;
  },
  linkAxisCourse: async (axisId: string, courseId: string): Promise<any> => {
    const response = await api.patch(`/staff/business/axes/${axisId}/course`, { courseId });
    return response.data;
  },
  listInvites: async (formId: string): Promise<any[]> => {
    const response = await api.get(`/staff/business/form/${formId}/invites`);
    return response.data;
  },
  createInvites: async (
    formId: string,
    data: { phones?: string[]; count?: number; label?: string; maxUses?: number },
    /** `invalid` و `duplicate` شماره‌هایی هستند که لینک نگرفتند */
  ): Promise<{ created: number; invalid?: string[]; duplicate?: string[] }> => {
    const response = await api.post(`/staff/business/form/${formId}/invites`, data);
    return response.data;
  },
  updateInvite: async (inviteId: string, data: { isActive?: boolean }): Promise<void> => {
    await api.patch(`/staff/business/invites/${inviteId}`, data);
  },
  deleteInvite: async (inviteId: string): Promise<void> => {
    await api.delete(`/staff/business/invites/${inviteId}`);
  },
};

// ─── پرسشنامه استخدام ─────────────────────────────────────────────────────────

export type RecruitmentSubmissionStatus = 'NEW' | 'EVALUATED' | 'DECIDED';
export type RecruitmentSuggestedResult =
  | 'VERY_SUITABLE'
  | 'SUITABLE_FOR_FOLLOWUP'
  | 'NEEDS_MORE_REVIEW'
  | 'LOW_FIT';

export interface RecruitmentCriterion {
  id: string;
  key: string;
  title: string;
  guideQuestion: string;
  weight: number;
  sortOrder: number;
}

export interface RecruitmentSettings {
  introText?: string | null;
  privacyNote?: string | null;
  positiveSignals?: string | null;
  reviewFlags?: string | null;
  scoringNote?: string | null;
}

export interface RecruitmentSubmissionRow {
  id: string;
  status: RecruitmentSubmissionStatus;
  fullName: string;
  lastName: string;
  mobile: string;
  positionTitle?: string | null;
  createdAt: string;
  overallScore: number | null;
  aiSuggestedResult: RecruitmentSuggestedResult | null;
  finalDecision: RecruitmentSuggestedResult | null;
}

export interface RecruitmentSubmissionDetail extends Omit<RecruitmentSubmissionRow, never> {
  fatherName?: string | null;
  birthDate?: string | null;
  birthPlace?: string | null;
  nationality?: string | null;
  nationalId?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  maritalStatus?: string | null;
  answers: Record<string, any>;
  decisionNote?: string | null;
  decidedAt?: string | null;
  evaluation: {
    overallScore: number;
    aiSuggestedResult: RecruitmentSuggestedResult;
    positiveSignalsFound: string[];
    redFlagsFound: string[];
    strengths: string[];
    risks: string[];
    verifyInInterview: string[];
    reasoning: string;
    evaluatedAt: string;
    scores: Array<{
      criterionId: string;
      key: string;
      title: string;
      weight: number;
      score: number;
      rationale: string;
    }>;
  } | null;
}

export const staffRecruitmentService = {
  listCriteria: async (): Promise<RecruitmentCriterion[]> => {
    const response = await api.get('/staff/recruitment/criteria');
    return response.data;
  },
  saveCriteria: async (
    criteria: Array<{ id: string; title: string; guideQuestion: string; weight: number }>,
  ): Promise<RecruitmentCriterion[]> => {
    const response = await api.patch('/staff/recruitment/criteria', { criteria });
    return response.data;
  },
  getSettings: async (): Promise<RecruitmentSettings> => {
    const response = await api.get('/staff/recruitment/settings');
    return response.data;
  },
  saveSettings: async (data: RecruitmentSettings): Promise<RecruitmentSettings> => {
    const response = await api.patch('/staff/recruitment/settings', data);
    return response.data;
  },
  listSubmissions: async (params?: {
    status?: RecruitmentSubmissionStatus;
    search?: string;
    take?: number;
    skip?: number;
  }): Promise<{ total: number; submissions: RecruitmentSubmissionRow[] }> => {
    const response = await api.get('/staff/recruitment/submissions', { params });
    return response.data;
  },
  getSubmission: async (id: string): Promise<RecruitmentSubmissionDetail> => {
    const response = await api.get(`/staff/recruitment/submissions/${id}`);
    return response.data;
  },
  evaluate: async (id: string): Promise<RecruitmentSubmissionDetail> => {
    const response = await api.post(`/staff/recruitment/submissions/${id}/evaluate`);
    return response.data;
  },
  saveDecision: async (
    id: string,
    data: { finalDecision: RecruitmentSuggestedResult; decisionNote?: string },
  ): Promise<void> => {
    await api.patch(`/staff/recruitment/submissions/${id}/decision`, data);
  },
  exportExcel: async (params?: { status?: RecruitmentSubmissionStatus; search?: string }): Promise<Blob> => {
    const response = await api.get('/staff/recruitment/submissions/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

// ─── پرتال رشد ۶C ─────────────────────────────────────────────────────────────

export type PortalHealth = 'GREEN' | 'YELLOW' | 'RED';

export type PortalTaskStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'NEEDS_REVISION'
  | 'APPROVED'
  | 'OVERDUE';

export type PortalFieldKind =
  | 'SHORT_TEXT' | 'LONG_TEXT' | 'NUMBER' | 'DATE'
  | 'FILE' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'LINK'
  | 'SINGLE_SELECT' | 'MULTI_SELECT' | 'RATING' | 'CHECKLIST'
  | 'TABLE' | 'CONFIRM' | 'QUIZ' | 'PEER_FEEDBACK' | 'TEMPLATE_UPLOAD';

export type PortalMetricDirection = 'UP' | 'DOWN' | 'NEUTRAL';
export type PortalMetricFormat = 'NUMBER' | 'CURRENCY' | 'PERCENT' | 'DURATION';

/** شاخصی که چند فیلد عددی در تسک‌های مختلف را به یک سری زمانی وصل می‌کند */
export interface PortalMetric {
  id: string;
  programId: string;
  key: string;
  title: string;
  unit?: string | null;
  direction: PortalMetricDirection;
  format: PortalMetricFormat;
  isPinned: boolean;
  isActive: boolean;
  sortOrder: number;
  /** تعداد فیلدهای تسک وصل‌شده — یعنی تعداد نقاط بالقوهٔ نمودار */
  linkedFieldCount?: number;
}

/** فقط این دو نوع فیلد می‌توانند به شاخص وصل شوند (هم‌راستا با سرور) */
export const METRIC_CAPABLE_KINDS: PortalFieldKind[] = ['NUMBER', 'RATING'];

/** یک نقطهٔ نمودار = یک تسک */
export interface PortalMetricPoint {
  taskId: string;
  title: string;
  dueAt: string | null;
  count: number;
  avg: number;
  sum: number;
  min: number;
  max: number;
  median: number;
}

export interface PortalMetricParticipantRow {
  participantId: string;
  name: string;
  cohort: string;
  /** هم‌ترتیب با points؛ null یعنی برای آن تسک پاسخی نداده */
  values: Array<number | null>;
  latest: number | null;
  change: number | null;
  changePercent: number | null;
}

export interface PortalMetricSeries {
  metric: PortalMetric;
  points: PortalMetricPoint[];
  participants: PortalMetricParticipantRow[];
}

export interface PortalMetricCard {
  metric: PortalMetric;
  current: number | null;
  previous: number | null;
  change: number | null;
  changePercent: number | null;
  pointCount: number;
  spark: number[];
}

export interface PortalSeriesParams {
  metricId: string;
  cohortId?: string;
  participantId?: string;
  onlyApproved?: boolean;
  from?: string;
  to?: string;
}

export interface PortalTaskField {
  id?: string;
  kind: PortalFieldKind;
  label: string;
  helpText?: string | null;
  required: boolean;
  sortOrder?: number;
  config?: Record<string, any> | null;
  /** شاخصی که این فیلد می‌سنجد؛ null یعنی هیچ‌کدام */
  metricId?: string | null;
}

export interface PortalTask {
  id: string;
  title: string;
  description?: string | null;
  sessionId?: string | null;
  trackId?: string | null;
  audience: 'INDIVIDUAL' | 'COHORT' | 'PROGRAM';
  dueAt?: string | null;
  requiresReview: boolean;
  allowResubmit: boolean;
  allowEditAfterSubmit: boolean;
  maxScore?: number | null;
  isPublished: boolean;
  fields: PortalTaskField[];
  session?: { id: string; number: number; title: string } | null;
  track?: { id: string; title: string } | null;
  _count?: { assignments: number };
  /** تعداد تخصیص‌هایی که دیگر NOT_STARTED نیستند — یعنی چیزی روی تسک ثبت شده */
  submittedCount?: number;
}

/** تسک از دید منتور: تعریف تسک + شمارش وضعیت شرکت‌کنندگان خودش */
export interface PortalMentorTask {
  id: string;
  title: string;
  description?: string | null;
  dueAt?: string | null;
  isPublished: boolean;
  audience: 'INDIVIDUAL' | 'COHORT' | 'PROGRAM';
  requiresReview: boolean;
  session?: { id: string; number: number; title: string } | null;
  track?: { id: string; title: string; color?: string | null } | null;
  fields: Array<{ id: string; kind: PortalFieldKind; label: string; required: boolean }>;
  myCounts: Partial<Record<PortalTaskStatus, number>>;
  myAssigned: number;
  myAwaitingReview: number;
  myOverdue: number;
  myApproved: number;
}

export interface PortalRosterRow {
  id: string;
  health: PortalHealth;
  progressPercent: number;
  lastActiveAt?: string | null;
  overdueCount: number;
  awaitingReview: number;
  user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null };
  cohort: { id: string; name: string };
  mentor?: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

export type PortalMessageFileKind = 'FILE' | 'IMAGE' | 'AUDIO';

export interface PortalMessageAttachment {
  id: string;
  kind: PortalMessageFileKind;
  name: string;
  mimeType?: string | null;
  sizeBytes: number;
  durationSec?: number | null;
}

export interface PortalMessage {
  id: string;
  body?: string | null;
  fromParticipant: boolean;
  readAt?: string | null;
  createdAt: string;
  author?: { id: string; name?: string | null } | null;
  attachments: PortalMessageAttachment[];
}

export interface PortalConversationRow {
  participantId: string;
  user: { id: string; firstName?: string | null; lastName?: string | null; phone?: string | null; avatar?: string | null };
  cohort: { id: string; name: string } | null;
  health: PortalHealth;
  lastMessageAt?: string | null;
  lastMessage?: { body?: string | null; fromParticipant: boolean; createdAt: string } | null;
  unread: number;
}

export const portalAdminService = {
  getProgram: async (): Promise<any> => (await api.get('/admin/portal/program')).data,
  getOverview: async (): Promise<any> => (await api.get('/admin/portal/overview')).data,
  updateProgram: async (programId: string, data: any) =>
    (await api.patch('/admin/portal/program/' + programId, data)).data,

  createSession: async (programId: string, data: any) =>
    (await api.post('/admin/portal/program/' + programId + '/sessions', data)).data,
  updateSession: async (sessionId: string, data: any) =>
    (await api.patch('/admin/portal/sessions/' + sessionId, data)).data,
  deleteSession: async (sessionId: string) => {
    await api.delete('/admin/portal/sessions/' + sessionId);
  },

  addMaterial: async (sessionId: string, form: FormData) =>
    (
      await api.post('/admin/portal/sessions/' + sessionId + '/materials', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data,
  deleteMaterial: async (materialId: string) => {
    await api.delete('/admin/portal/materials/' + materialId);
  },

  createCohort: async (programId: string, data: any) =>
    (await api.post('/admin/portal/program/' + programId + '/cohorts', data)).data,
  updateCohort: async (cohortId: string, data: any) =>
    (await api.patch('/admin/portal/cohorts/' + cohortId, data)).data,
  deleteCohort: async (cohortId: string) => {
    await api.delete('/admin/portal/cohorts/' + cohortId);
  },
  addParticipants: async (programId: string, cohortId: string, data: any) =>
    (
      await api.post(
        '/admin/portal/program/' + programId + '/cohorts/' + cohortId + '/participants',
        data,
      )
    ).data,
  /** فایل فقط خوانده می‌شود و شماره‌ها برمی‌گردند؛ افزودن مرحلهٔ جداست */
  extractPhonesFromFile: async (
    file: File,
  ): Promise<{
    phones: string[];
    invalid: string[];
    duplicates: number;
    scannedCells: number;
  }> => {
    const form = new FormData();
    form.append('file', file);
    return (
      await api.post('/admin/portal/participants/extract-phones', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  },
  moveParticipant: async (participantId: string, data: any) =>
    (await api.patch('/admin/portal/participants/' + participantId, data)).data,
  listParticipants: async (params?: {
    cohortId?: string;
    health?: string;
  }): Promise<PortalRosterRow[]> =>
    (await api.get('/admin/portal/participants', { params })).data,
  listMentors: async () => (await api.get('/admin/portal/mentors')).data,

  getMetricSeries: async (params: PortalSeriesParams): Promise<PortalMetricSeries> =>
    (await api.get('/admin/portal/analytics/series', { params })).data,
  getMetricSummary: async (
    programId?: string,
    onlyApproved?: boolean,
  ): Promise<PortalMetricCard[]> =>
    (await api.get('/admin/portal/analytics/summary', { params: { programId, onlyApproved } })).data,
  /** CSV را به‌صورت blob می‌گیرد تا مرورگر بتواند ذخیره‌اش کند */
  downloadMetricCsv: async (params: PortalSeriesParams): Promise<Blob> =>
    (await api.get('/admin/portal/analytics/export', { params, responseType: 'blob' })).data,

  listMetrics: async (programId?: string): Promise<PortalMetric[]> =>
    (await api.get('/admin/portal/metrics', { params: { programId } })).data,
  createMetric: async (data: Partial<PortalMetric>, programId?: string): Promise<PortalMetric> =>
    (await api.post('/admin/portal/metrics', data, { params: { programId } })).data,
  updateMetric: async (metricId: string, data: Partial<PortalMetric>): Promise<PortalMetric> =>
    (await api.patch('/admin/portal/metrics/' + metricId, data)).data,
  deleteMetric: async (metricId: string, force = false) => {
    await api.delete('/admin/portal/metrics/' + metricId, { params: force ? { force: true } : {} });
  },

  listTasks: async (programId: string, sessionId?: string): Promise<PortalTask[]> =>
    (await api.get('/admin/portal/program/' + programId + '/tasks', { params: { sessionId } })).data,
  getTask: async (taskId: string): Promise<PortalTask> =>
    (await api.get('/admin/portal/tasks/' + taskId)).data,
  createTask: async (programId: string, data: any): Promise<PortalTask> =>
    (await api.post('/admin/portal/program/' + programId + '/tasks', data)).data,
  updateTask: async (taskId: string, data: any): Promise<PortalTask> =>
    (await api.patch('/admin/portal/tasks/' + taskId, data)).data,
  /** force فقط وقتی فرستاده می‌شود که کاربر حذفِ تسکِ پاسخ‌دار را تأیید کرده باشد */
  deleteTask: async (taskId: string, force = false) => {
    await api.delete('/admin/portal/tasks/' + taskId, {
      params: force ? { force: 'true' } : undefined,
    });
  },
  publishTask: async (taskId: string, data: any) =>
    (await api.post('/admin/portal/tasks/' + taskId + '/publish', data)).data,

  listTickets: async (params?: { status?: string; search?: string }) =>
    (await api.get('/admin/portal/tickets', { params })).data,
  replyTicket: async (ticketId: string, body: string, isInternal = false) =>
    (await api.post('/admin/portal/tickets/' + ticketId + '/reply', { body, isInternal })).data,
  updateTicket: async (ticketId: string, data: any) =>
    (await api.patch('/admin/portal/tickets/' + ticketId, data)).data,

  announce: async (programId: string, data: any) =>
    (await api.post('/admin/portal/program/' + programId + '/announce', data)).data,
  runAlerts: async () => (await api.post('/admin/portal/alerts/run')).data,
};

export const portalMentorService = {
  roster: async (params?: { cohortId?: string; health?: string }): Promise<PortalRosterRow[]> =>
    (await api.get('/portal/mentor/roster', { params })).data,
  participant: async (participantId: string) =>
    (await api.get('/portal/mentor/participants/' + participantId)).data,
  addNote: async (participantId: string, body: string) =>
    (await api.post('/portal/mentor/participants/' + participantId + '/notes', { body })).data,
  deleteNote: async (noteId: string) => {
    await api.delete('/portal/mentor/notes/' + noteId);
  },
  reviewQueue: async (cohortId?: string) =>
    (await api.get('/portal/mentor/review-queue', { params: { cohortId } })).data,
  assignment: async (assignmentId: string) =>
    (await api.get('/portal/mentor/assignments/' + assignmentId)).data,
  review: async (assignmentId: string, data: any) =>
    (await api.post('/portal/mentor/assignments/' + assignmentId + '/review', data)).data,
  questions: async (status?: string) =>
    (await api.get('/portal/mentor/questions', { params: { status } })).data,
  replyQuestion: async (questionId: string, body: string) =>
    (await api.post('/portal/mentor/questions/' + questionId + '/reply', { body })).data,
  escalateQuestion: async (questionId: string, escalatedToId: string, note?: string) =>
    (await api.post('/portal/mentor/questions/' + questionId + '/escalate', {
      escalatedToId,
      note,
    })).data,
  mentors: async () => (await api.get('/portal/mentor/mentors')).data,

  /** همهٔ تسک‌های دوره + وضعیت شرکت‌کنندگان همین منتور */
  tasks: async (): Promise<PortalMentorTask[]> =>
    (await api.get('/portal/mentor/tasks')).data,
  /** تاریخچهٔ تیکت‌ها؛ status نداده = همه */
  tickets: async (params?: { status?: string; search?: string }) =>
    (await api.get('/portal/mentor/tickets', { params })).data,
  /** گروه‌های خودِ منتور برای انتخاب مخاطب پیام */
  cohorts: async (): Promise<Array<{ id: string; name: string; capacity: number; participants: number }>> =>
    (await api.get('/portal/mentor/cohorts')).data,
  announce: async (data: {
    title: string;
    body?: string;
    linkPath?: string;
    cohortIds?: string[];
    /** اگر پر باشد، cohortIds نادیده گرفته می‌شود */
    participantIds?: string[];
    alsoSms?: boolean;
  }): Promise<{ recipients: number; sent: number; phones: string[] }> =>
    (await api.post('/portal/mentor/announce', data)).data,

  // ── گفتگوی دوطرفه ─────────────────────────────────────────────────────────

  conversations: async (search?: string): Promise<PortalConversationRow[]> =>
    (await api.get('/portal/mentor/conversations', { params: search ? { search } : undefined }))
      .data,

  thread: async (
    participantId: string,
    before?: string,
  ): Promise<{ messages: PortalMessage[]; hasMore: boolean }> =>
    (
      await api.get('/portal/mentor/participants/' + participantId + '/messages', {
        params: before ? { before } : undefined,
      })
    ).data,

  sendMessage: async (
    participantId: string,
    data: { body?: string; attachmentOnly?: boolean },
  ): Promise<PortalMessage> =>
    (await api.post('/portal/mentor/participants/' + participantId + '/messages', data)).data,

  attachToMessage: async (
    messageId: string,
    file: File,
    durationSec?: number,
  ): Promise<PortalMessageAttachment> => {
    const form = new FormData();
    form.append('file', file);
    if (durationSec !== undefined) form.append('durationSec', String(Math.round(durationSec)));
    return (await api.post('/portal/mentor/messages/' + messageId + '/attachments', form)).data;
  },

  /**
   * پیوست را به‌صورت blob می‌گیرد نه لینک مستقیم: مسیر پشت JWT است و تگ
   * `<audio src>` هدر Authorization نمی‌فرستد.
   */
  attachmentUrl: async (fileId: string): Promise<string> => {
    const res = await api.get('/portal/mentor/attachments/' + fileId, {
      responseType: 'blob',
    });
    return URL.createObjectURL(res.data as Blob);
  },

  /** شاخص‌های دوره برای انتخاب در نمودار روند تیم */
  listMetrics: async (): Promise<PortalMetric[]> =>
    (await api.get('/portal/mentor/metrics')).data,
  /**
   * روند شاخص برای تیم منتور.
   * دامنه روی سرور قفل است؛ حتی اگر participantId فرد دیگری فرستاده شود
   * نتیجه خالی برمی‌گردد.
   */
  getMetricSeries: async (params: PortalSeriesParams): Promise<PortalMetricSeries> =>
    (await api.get('/portal/mentor/analytics/series', { params })).data,

  /** دانلود فایل پاسخ از مسیر احرازهویت‌شده */
  downloadFile: async (fileId: string, filename: string) => {
    const response = await api.get('/portal/files/' + fileId + '/download', {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
